import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { RegistrationMode } from '@shared/types';
import { useAuthStore } from '../app/authStore';
import { ApiError, describeError } from '../lib/api';
import { usePlatformFlags } from '../lib/platformFlags';
import { Button } from '../components/ui';
import { Checkbox, Field, Input, Notice, PasswordInput } from '../components/form';
import { Icon } from '../components/Icon';
import { Turnstile } from '../components/Turnstile';

/**
 * 自助注册。
 *
 * 个人与企业走同一条接口，差别只有三件事：
 *   - 企业需要组织名、联系人与手机号；
 *   - 企业创建后是 `pending`，要等运营审核；
 *   - 个人组织名由服务端生成（「某某的个人空间」）。
 */
export function RegisterPage() {
  const register = useAuthStore((state) => state.register);
  const navigate = useNavigate();
  const { flags } = usePlatformFlags();
  /** 运营关闭注册后整页置灰：表单仍可见（用户知道该功能存在），但点不动。 */
  const closed = !flags.registrationEnabled;

  const [mode, setMode] = useState<RegistrationMode>('personal');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [industry, setIndustry] = useState('');
  const [website, setWebsite] = useState('');
  const [agree, setAgree] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [devVerificationUrl, setDevVerificationUrl] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function switchMode(next: RegistrationMode) {
    setMode(next);
    setError(null);
    setFieldErrors({});
  }

  async function submit() {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      const session = await register({
        mode,
        name: name.trim(),
        email: email.trim(),
        password,
        agreeTerms: agree,
        turnstileToken,
        ...(mode === 'enterprise'
          ? {
              orgName: orgName.trim(),
              contactName: contactName.trim(),
              contactPhone: contactPhone.trim(),
              industry: industry.trim() || undefined,
              website: website.trim() || undefined,
            }
          : {}),
      });
      if (session.devVerificationUrl) {
        setDevVerificationUrl(session.devVerificationUrl);
        return;
      }
      navigate('/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFieldErrors(caught.fieldErrors);
        setError(caught.message);
      } else {
        setError(describeError(caught));
      }
    } finally {
      setBusy(false);
    }
  }

  if (devVerificationUrl) {
    return (
      <div className="flex min-h-full items-center justify-center bg-canvas px-4 py-10">
        <div className="w-full max-w-[420px] space-y-4">
          <h1 className="text-[28px] font-semibold text-fg">注册成功</h1>
          <Notice tone="info">
            当前使用本地日志邮件通道，验证链接不会真的发出。开发环境可直接打开下面的链接：
          </Notice>
          <a
            href={devVerificationUrl}
            className="block break-all rounded-control bg-nest-2 px-3 py-2 text-xs text-brand hover:underline"
          >
            {devVerificationUrl}
          </a>
          <div className="flex gap-3">
            <Button variant="primary" block onClick={() => navigate('/', { replace: true })}>
              稍后验证，先进入控制台
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-[460px]">
        <div className="mb-6 flex items-center gap-2">
          <span className="text-[15px] font-semibold text-fg">Havorix</span>
          <span className="text-[11px] text-fg-subtle">™</span>
        </div>

        <h1 className="mb-1 text-[32px] leading-[42px] font-semibold text-fg">创建账号</h1>
        <p className="mb-6 text-[13px] text-fg-muted">
          个人账号注册后即可使用；企业账号需通过资质审核后才能邀请成员与开票。
        </p>

        {closed ? (
          <div className="mb-4">
            <Notice tone="warn">
              暂未开放注册。你仍可以登录已有账号；需要新账号请联系客服。
            </Notice>
          </div>
        ) : null}

        <div className="mb-5 flex gap-6 text-base">
          {(
            [
              ['personal', '个人'],
              ['enterprise', '企业'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              disabled={closed}
              onClick={() => switchMode(id)}
              className={`border-b-2 pb-2 transition-colors ${
                mode === id
                  ? 'border-brand font-medium text-brand'
                  : 'border-transparent text-fg-muted hover:text-fg'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {error ? (
          <div className="mb-4">
            <Notice tone="danger">{error}</Notice>
          </div>
        ) : null}

        <div className="space-y-4">
          {mode === 'enterprise' ? (
            <Field label="企业名称" htmlFor="orgName" error={fieldErrors.orgName} required>
              <Input
                id="orgName"
                value={orgName}
                onChange={setOrgName}
                placeholder="营业执照上的企业名称"
                autoComplete="organization"
                maxLength={60}
                disabled={closed}
              />
            </Field>
          ) : null}

          <Field label="姓名" htmlFor="name" error={fieldErrors.name} required>
            <Input id="name" value={name} onChange={setName} placeholder="你的姓名" autoComplete="name" disabled={closed} />
          </Field>

          <Field label="邮箱" htmlFor="email" error={fieldErrors.email} required>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="name@example.com"
              autoComplete="email"
              disabled={closed}
            />
          </Field>

          <Field
            label="口令"
            htmlFor="password"
            error={fieldErrors.password}
            hint="至少 10 位，包含字母与数字"
            required
          >
            <PasswordInput
              id="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              disabled={closed}
            />
          </Field>

          {mode === 'enterprise' ? (
            <>
              <Field label="联系人" htmlFor="contactName" error={fieldErrors.contactName} required>
                <Input
                  id="contactName"
                  value={contactName}
                  onChange={setContactName}
                  placeholder="审核时的联系人"
                  autoComplete="name"
                  disabled={closed}
                />
              </Field>
              <Field label="联系电话" htmlFor="contactPhone" error={fieldErrors.contactPhone} required>
                <Input
                  id="contactPhone"
                  type="tel"
                  value={contactPhone}
                  onChange={setContactPhone}
                  placeholder="手机号"
                  autoComplete="tel"
                  disabled={closed}
                />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="行业" htmlFor="industry" error={fieldErrors.industry}>
                  <Input id="industry" value={industry} onChange={setIndustry} placeholder="选填" disabled={closed} />
                </Field>
                <Field label="网址" htmlFor="website" error={fieldErrors.website}>
                  <Input id="website" value={website} onChange={setWebsite} placeholder="选填" disabled={closed} />
                </Field>
              </div>
            </>
          ) : null}

          <Checkbox
            checked={agree}
            onChange={setAgree}
            disabled={closed}
            label={
              <span>
                我已阅读并同意
                <a
                  href="https://havorix.cn/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="mx-1 text-brand hover:underline"
                >
                  服务条款
                </a>
                与
                <a
                  href="https://havorix.cn/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="mx-1 text-brand hover:underline"
                >
                  隐私政策
                </a>
              </span>
            }
          />
          {fieldErrors.agreeTerms ? (
            <p className="text-xs text-danger">{fieldErrors.agreeTerms}</p>
          ) : null}

          <Turnstile onToken={setTurnstileToken} />

          <Button variant="primary" block loading={busy} onClick={submit} disabled={closed}>
            创建账号
          </Button>

          <p className="flex items-center justify-center gap-1 text-[13px] text-fg-muted">
            已有账号？
            <Link to="/login" className="text-brand hover:underline">
              直接登录
            </Link>
          </p>
        </div>

        <p className="mt-6 flex items-start gap-1.5 text-xs text-fg-subtle">
          <Icon name="shield" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          企业账号在审核通过前不能邀请成员、生成计费 API Key 或申请发票；下单与付款不受影响。
        </p>
      </div>
    </div>
  );
}
